import { createBrowserRouter, Navigate } from "react-router";
import { FullPageSpinner } from "../components/States";
import { RootLayout } from "../layouts/RootLayout";
import { RouteErrorPage } from "../pages/RouteErrorPage";
import { crumb, projectCrumb, taskCrumb } from "./crumbs";
import { GuestOnly, LandingGate, RequireAuth } from "./guards";

/** Route modules are lazy-loaded so each page ships in its own chunk. */
export function createAppRouter() {
  return createBrowserRouter([
    {
      element: <RootLayout />,
      errorElement: <RouteErrorPage />,
      // Shown while the first lazy route module loads.
      hydrateFallbackElement: <FullPageSpinner />,
      children: [
        {
          element: <LandingGate />,
          children: [
            {
              index: true,
              lazy: () =>
                import("../pages/LandingPage").then((m) => ({ Component: m.LandingPage })),
            },
          ],
        },
        {
          element: <GuestOnly />,
          children: [
            {
              path: "login",
              lazy: () =>
                import("../pages/auth/LoginPage").then((m) => ({ Component: m.LoginPage })),
            },
            {
              path: "register",
              lazy: () =>
                import("../pages/auth/RegisterPage").then((m) => ({ Component: m.RegisterPage })),
            },
            {
              path: "forgot-password",
              lazy: () =>
                import("../pages/auth/ForgotPasswordPage").then((m) => ({
                  Component: m.ForgotPasswordPage,
                })),
            },
          ],
        },
        {
          path: "reset-password",
          lazy: () =>
            import("../pages/auth/ResetPasswordPage").then((m) => ({
              Component: m.ResetPasswordPage,
            })),
        },
        {
          path: "verify-email",
          lazy: () =>
            import("../pages/auth/VerifyEmailPage").then((m) => ({ Component: m.VerifyEmailPage })),
        },
        {
          path: "invite/:token",
          lazy: () => import("../pages/InvitePage").then((m) => ({ Component: m.InvitePage })),
        },
        {
          element: <RequireAuth />,
          children: [
            {
              path: "onboarding",
              lazy: () =>
                import("../pages/OnboardingPage").then((m) => ({ Component: m.OnboardingPage })),
            },
            {
              path: "join",
              lazy: () =>
                import("../pages/JoinWorkspacePage").then((m) => ({
                  Component: m.JoinWorkspacePage,
                })),
            },
            {
              path: "account",
              lazy: () =>
                import("../pages/AccountPage").then((m) => ({ Component: m.AccountPage })),
            },
            {
              path: "w/:workspaceSlug",
              lazy: () =>
                import("../layouts/WorkspaceLayout").then((m) => ({
                  Component: m.WorkspaceLayout,
                })),
              children: [
                {
                  index: true,
                  lazy: () =>
                    import("../pages/workspace/WorkspaceHomePage").then((m) => ({
                      Component: m.WorkspaceHomePage,
                    })),
                },
                {
                  path: "projects",
                  handle: crumb("Projects"),
                  children: [
                    {
                      index: true,
                      lazy: () =>
                        import("../pages/workspace/ProjectsPage").then((m) => ({
                          Component: m.ProjectsPage,
                        })),
                    },
                    {
                      // A full task page: its own route so it is not nested in the project tabs.
                      path: ":projectKey/tasks/:taskKey",
                      handle: taskCrumb,
                      lazy: () =>
                        import("../pages/workspace/TaskDetailPage").then((m) => ({
                          Component: m.TaskDetailPage,
                        })),
                    },
                    {
                      path: ":projectKey",
                      handle: projectCrumb,
                      lazy: () =>
                        import("../pages/workspace/ProjectLayout").then((m) => ({
                          Component: m.ProjectLayout,
                        })),
                      children: [
                        {
                          index: true,
                          element: <Navigate to="overview" replace />,
                        },
                        {
                          path: "list",
                          handle: crumb("List"),
                          lazy: () =>
                            import("../pages/workspace/project/ProjectListTab").then((m) => ({
                              Component: m.ProjectListTab,
                            })),
                        },
                        {
                          path: "board",
                          handle: crumb("Board"),
                          lazy: () =>
                            import("../pages/workspace/project/ProjectBoardTab").then((m) => ({
                              Component: m.ProjectBoardTab,
                            })),
                        },
                        {
                          path: "workload",
                          handle: crumb("Workload"),
                          lazy: () =>
                            import("../pages/workspace/project/ProjectWorkloadTab").then((m) => ({
                              Component: m.ProjectWorkloadTab,
                            })),
                        },
                        {
                          path: "channel",
                          handle: crumb("Channel"),
                          lazy: () =>
                            import("../pages/workspace/project/ProjectChannelTab").then((m) => ({
                              Component: m.ProjectChannelTab,
                            })),
                        },
                        {
                          path: "members",
                          handle: crumb("Members"),
                          lazy: () =>
                            import("../pages/workspace/project/ProjectMembersTab").then((m) => ({
                              Component: m.ProjectMembersTab,
                            })),
                        },
                        {
                          path: "overview",
                          handle: crumb("Overview"),
                          lazy: () =>
                            import("../pages/workspace/project/ProjectOverviewTab").then((m) => ({
                              Component: m.ProjectOverviewTab,
                            })),
                        },
                      ],
                    },
                  ],
                },
                {
                  path: "chat/:channelId?",
                  handle: crumb("Chat"),
                  lazy: () =>
                    import("../pages/workspace/ChatPage").then((m) => ({
                      Component: m.ChatPage,
                    })),
                },
                {
                  path: "settings",
                  children: [
                    {
                      index: true,
                      handle: crumb("Settings"),
                      lazy: () =>
                        import("../pages/workspace/WorkspaceSettingsPage").then((m) => ({
                          Component: m.WorkspaceSettingsPage,
                        })),
                    },
                    {
                      path: "members",
                      handle: crumb("Members"),
                      lazy: () =>
                        import("../pages/workspace/MembersPage").then((m) => ({
                          Component: m.MembersPage,
                        })),
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: "*",
          lazy: () => import("../pages/NotFoundPage").then((m) => ({ Component: m.NotFoundPage })),
        },
      ],
    },
  ]);
}
